import React from 'react';
import {
  Typography,
  Chip,
  Box,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  TableBody,
  TableRow,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import {
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
} from '../../common/Table/Table.styles';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { DetailModal } from '../../ui/Modal';
import { ProductHistoryDetailResponseDto } from '../../../api/models/ProductHistoryDetailResponseDto';
import {
  EVENT_TYPE_COLORS,
  getEventTypeLabel,
  formatHistoryDate,
  getHistoryProductCodesFromNewValue,
} from '../../../utils/productHistory.utils';
import { generateHistoryMessage } from '../../../utils/productHistoryMessageGenerator';
import { useProductMovementHistoryDetail } from './ProductMovementHistory.hooks';
import {
  useRelatedEntityNavigation,
  type RelatedEntityNavigationExtra,
} from '../../../hooks/useRelatedEntityNavigation';

export interface ProductMovementHistoryDetailModalProps {
  open: boolean;
  onClose: () => void;
  historyId: number | null;
}

/** Tarjetas de sección: legibles en claro y oscuro (evita grey.50 / texto invisible). */
const detailModalSectionPaperSx: SxProps<Theme> = (theme) => ({
  p: 2,
  mb: 3,
  bgcolor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  backgroundImage: 'none',
});

const entityLinkButtonSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  font: 'inherit',
  textAlign: 'left',
  '&:hover': {
    color: theme.palette.primary.main,
  },
});

const ProductMovementHistoryDetailModal: React.FC<ProductMovementHistoryDetailModalProps> = ({
  open,
  onClose,
  historyId,
}) => {
  const { data, isLoading, error } = useProductMovementHistoryDetail(historyId);
  const { navigateToRelatedEntity } = useRelatedEntityNavigation();

  const groupedProductCodes = getHistoryProductCodesFromNewValue(data?.newValue);
  const hasProductSectionContent = Boolean(
    data?.product || groupedProductCodes.length > 0 || data?.location,
  );

  const getEventTypeColor = (eventType: ProductHistoryDetailResponseDto.eventType): string => {
    return EVENT_TYPE_COLORS[eventType] || '#6B7280';
  };

  const handleRelatedEntityClick = (
    type: string | undefined,
    id: number | undefined,
    code: string | undefined,
    extra?: RelatedEntityNavigationExtra,
  ) => {
    navigateToRelatedEntity(type, id, code, extra);
    onClose(); // Cerrar el modal de historial al navegar
  };

  // Filtrar campos según el tipo de evento
  const getFilteredChangedFields = (): string[] => {
    if (!data?.eventType) {
      return [];
    }

    if (data.eventType === 'SAFETY_STOCK_UPDATED_INDIVIDUAL') {
      return ['productCode', 'productName', 'locationName', 'safetyStock'];
    }
    if (data.eventType === 'ORDER_REFUNDED' || data.eventType === 'ORDER_PARTIALLY_REFUNDED') {
      return [
        'refundAmount',
        'restockingFee',
        'itemsReturned',
        'returnReason',
        'returnNotes',
        'status',
      ];
    }
    if (data.eventType === 'SAFETY_STOCK_UPDATED_BULK') {
      const isBulkSelection =
        data.newValue?.source === 'BULK_SELECTION' ||
        data.relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION';
      if (isBulkSelection) {
        return ['scopeDescription', 'safetyStock', 'recordsUpdated', 'selectionCount'];
      }
      return [
        'scope',
        'scopeDescription',
        'locationName',
        'locationId',
        'categoryName',
        'categoryId',
        'productIds',
        'safetyStock',
        'recordsUpdated',
      ];
    }

    if (!data.changedFields) {
      return [];
    }

    // Para eventos de propiedades, solo mostrar campos relevantes
    const propertyEvents = ['PROPERTY_ADDED', 'PROPERTY_UPDATED', 'PROPERTY_DELETED'];

    if (propertyEvents.includes(data.eventType)) {
      // Campos permitidos para eventos de propiedades
      const allowedPropertyFields = [
        'propertyType',
        'propertyName',
        'propertyId',
        'propertyCode',
        'propertyColor',
        'properties', // Array de propiedades (para múltiples etiquetas)
        'name',
        'description',
        'color',
        'usageFrequency',
        'previousName',
        'previousDescription',
        'previousColor',
        'previousUsageFrequency',
        'source',
        'materialId',
        'tagIds',
        'tagNames',
        'propertyValue',
      ];

      const filtered = data.changedFields.filter((field) => allowedPropertyFields.includes(field));

      // Fallback: si no hay campos filtrados pero existen datos de propiedad, mostrar propertyName/propertyValue
      if (
        filtered.length === 0 &&
        (data.newValue?.propertyValue !== undefined ||
          data.previousValue?.propertyValue !== undefined ||
          data.newValue?.propertyName ||
          data.previousValue?.propertyName)
      ) {
        return ['propertyName', 'propertyValue'];
      }

      return filtered;
    }

    // Para otros eventos, mostrar todos los campos
    return data.changedFields;
  };

  // Formatear nombre de campo para mostrar
  const formatFieldName = (field: string): string => {
    const fieldLabels: Record<string, string> = {
      propertyType: 'Tipo de Propiedad',
      propertyName: 'Nombre',
      propertyId: 'ID',
      propertyCode: 'Código',
      propertyColor: 'Color',
      properties: 'Propiedades',
      name: 'Nombre',
      description: 'Descripción',
      color: 'Color',
      usageFrequency: 'Frecuencia de Uso',
      previousName: 'Nombre Anterior',
      previousDescription: 'Descripción Anterior',
      previousColor: 'Color Anterior',
      previousUsageFrequency: 'Frecuencia Anterior',
      source: 'Origen',
      tagIds: 'Etiquetas',
      tagNames: 'Nombres de Etiquetas',
      materialId: 'Material',
      supplierId: 'Proveedor',
      propertyValue: 'Valor',
      // Propiedades físicas desde gestión de almacén
      weightGrams: 'Peso (gramos)',
      lengthMm: 'Largo (cm)',
      diameterSize: 'Talla',
      propertiesDescription: 'Descripción de Propiedades',
      condition: 'Condición',
      workshopCode: 'Código Proveedor',
      safetyStock: 'Stock de seguridad',
      productCode: 'Código de producto',
      productName: 'Nombre de producto',
      locationName: 'Sucursal',
      locationId: 'ID sucursal',
      scope: 'Alcance',
      scopeDescription: 'Descripción del alcance',
      recordsUpdated: 'Registros actualizados',
      selectionCount: 'Productos seleccionados',
      refundAmount: 'Monto reembolsado',
      restockingFee: 'Cargo por reposición',
      itemsReturned: 'Piezas devueltas',
      returnReason: 'Motivo de devolución',
      returnNotes: 'Notas de devolución',
      categoryName: 'Tipo de producto (categoría)',
      categoryId: 'ID categoría',
      productIds: 'IDs de productos',
      sessionCode: 'Código de sesión',
      expectedCount: 'Esperados',
      actualCount: 'Contados',
      successCount: 'Exitosos',
      missingCount: 'Faltantes',
      excessCount: 'Sobrantes',
      duplicateCount: 'Duplicados',
      scannedNotAvailableCount: 'No disponibles',
      comparedAt: 'Fecha de comparación',
      email: 'Correo',
      firstName: 'Nombre',
      lastName: 'Apellido',
      isActive: 'Activo',
      primaryLocationId: 'Sucursal principal',
      canSellFromOtherLocations: 'Ventas multi-sucursal',
      roleNames: 'Roles',
      totalRoles: 'Total de roles',
      addedRoleNames: 'Roles agregados',
      removedRoleNames: 'Roles removidos',
      addedCount: 'Cantidad agregada',
      removedCount: 'Cantidad removida',
      passwordUpdated: 'Contraseña actualizada',
    };

    return fieldLabels[field] || field;
  };

  // Formatear nombre de propiedad para propiedades agrupadas
  const formatPropertyName = (name: string): string => {
    const nameMap: Record<string, string> = {
      weightGrams: 'Peso (gramos)',
      lengthMm: 'Largo (cm)',
      diameterSize: 'Talla',
      propertiesDescription: 'Descripción de Propiedades',
      condition: 'Condición',
      color: 'Color',
      workshopCode: 'Código Proveedor',
      materialId: 'Material',
    };
    return nameMap[name] || name;
  };

  // Obtener valor formateado para mostrar
  const getFormattedValue = (value: any): string => {
    if (value === undefined || value === null) {
      return '-';
    }

    // Si es un array, formatear como lista
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '-';
      }
      // Si es un array de propiedades (etiquetas), formatear cada una
      if (value.length > 0 && typeof value[0] === 'object' && value[0].propertyName) {
        return value
          .map((prop: any) => {
            const parts: string[] = [];
            if (prop.propertyName) parts.push(prop.propertyName);
            if (prop.propertyId) parts.push(`(ID: ${prop.propertyId})`);
            return parts.join(' ');
          })
          .join(', ');
      }
      return value.map(String).join(', ');
    }

    // Si es un objeto, mostrar información relevante
    if (typeof value === 'object') {
      // Para objetos de propiedades, mostrar información clave
      if (value.propertyType) {
        const parts: string[] = [];
        if (value.propertyName) parts.push(value.propertyName);
        if (value.propertyCode) parts.push(`(${value.propertyCode})`);
        if (value.propertyColor) parts.push(`Color: ${value.propertyColor}`);
        return parts.length > 0 ? parts.join(' ') : JSON.stringify(value);
      }
      return JSON.stringify(value);
    }

    return String(value);
  };

  const formatRolePermissionItem = (permission: any): string => {
    if (!permission || typeof permission !== 'object') {
      return String(permission ?? '-');
    }

    const module = permission.module ?? '-';
    const menu = permission.menu ?? '-';
    const action = permission.action ?? '-';
    const resource = permission.resource ? ` (${permission.resource})` : '';
    return `${module} / ${menu} / ${action}${resource}`;
  };

  const formatRolePermissionList = (value: any): string => {
    if (!Array.isArray(value)) {
      return '-';
    }
    if (value.length === 0) {
      return 'Ninguno';
    }
    return value.map((permission) => formatRolePermissionItem(permission)).join(', ');
  };

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title="Detalles del Historial"
      icon={<HistoryIcon sx={{ mr: 1 }} />}
      maxWidth={false}
      paperSx={{
        width: 'min(1200px, calc(100vw - 64px))',
        maxWidth: '1200px',
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ py: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : data ? (
        <Box sx={{ py: 2 }}>
          {/* Header Section */}
          <Paper elevation={0} sx={detailModalSectionPaperSx}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de Evento
                  </Typography>
                </Box>
                <Chip
                  label={getEventTypeLabel(data.eventType, data.relatedEntityType, data.newValue)}
                  sx={{
                    backgroundColor: getEventTypeColor(data.eventType),
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Fecha y Hora
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={500} color="text.primary">
                  {formatHistoryDate(data.performedAt)}
                </Typography>
              </Grid>
              {data.performedBy && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Usuario
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.primary">
                    {data.performedBy.firstName} {data.performedBy.lastName}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Product Information */}
          {hasProductSectionContent && (
            <Paper elevation={0} sx={detailModalSectionPaperSx}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}
              >
                <InventoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                Información del Producto
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {data.product && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Producto
                      </Typography>
                      <Typography variant="body1" fontWeight={500} color="text.primary">
                        {data.product.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Código
                      </Typography>
                      <Typography variant="body1" fontFamily="monospace" color="text.primary">
                        {/* Mostrar código único si está disponible, sino mostrar código del catálogo */}
                        {data.warehouseProduct?.productCodeUnique ||
                          data.productCodeUnique ||
                          data.product.code}
                      </Typography>
                    </Grid>
                  </>
                )}
                {!data.product && groupedProductCodes.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {groupedProductCodes.length === 1 ? 'Producto' : 'Productos'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      {groupedProductCodes.map((code, index) => (
                        <Typography
                          key={`${code}-${index}`}
                          variant="body1"
                          fontFamily="monospace"
                          fontWeight={500}
                          color="text.primary"
                        >
                          {code}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}
                {data.location && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ubicación
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {data.location.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Related Entity */}
          {(data.transferCode ||
            data.orderCode ||
            data.recordCode ||
            data.relatedEntityType === 'SAFETY_STOCK' ||
            data.relatedEntityType === 'SAFETY_STOCK_BULK' ||
            data.relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION' ||
            data.relatedEntityType === 'PHYSICAL_COUNT') && (
            <Paper elevation={0} sx={detailModalSectionPaperSx}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}
              >
                <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                Entidad Relacionada
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {data.transferCode && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {data.relatedEntityType === 'INVENTORY_REQUEST'
                        ? 'Código SOL'
                        : 'Código de Transferencia'}
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick(
                          data.relatedEntityType === 'INVENTORY_REQUEST'
                            ? 'INVENTORY_REQUEST'
                            : 'INVENTORY_TRANSFER',
                          data.relatedEntityId,
                          data.transferCode,
                        )
                      }
                      sx={entityLinkButtonSx}
                    >
                      {data.transferCode}
                    </Typography>
                  </Grid>
                )}
                {data.orderCode && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Código de venta
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick(
                          'SALES_ORDER',
                          data.relatedEntityId,
                          data.orderCode,
                        )
                      }
                      sx={entityLinkButtonSx}
                    >
                      {data.orderCode}
                    </Typography>
                  </Grid>
                )}
                {data.recordCode && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Código de Registro
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick(
                          'INVENTORY_RECORD',
                          data.relatedEntityId,
                          data.recordCode,
                        )
                      }
                      sx={entityLinkButtonSx}
                    >
                      {data.recordCode}
                    </Typography>
                  </Grid>
                )}
                {data.relatedEntityType === 'SAFETY_STOCK' && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stock de seguridad
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick('SAFETY_STOCK', data.relatedEntityId, undefined, {
                          productId: data.product?.id,
                          locationId:
                            data.location?.id ?? (data.newValue?.locationId as number | undefined),
                          productCode:
                            data.product?.code ??
                            (data.newValue?.productCode as string | undefined),
                        })
                      }
                      sx={entityLinkButtonSx}
                    >
                      Ir a Stock de seguridad
                    </Typography>
                  </Grid>
                )}
                {data.relatedEntityType === 'SAFETY_STOCK_BULK' && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stock de seguridad (edición personalizada)
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick(
                          'SAFETY_STOCK_BULK',
                          data.relatedEntityId,
                          undefined,
                          {
                            locationId:
                              data.location?.id ??
                              (data.newValue?.locationId as number | undefined),
                          },
                        )
                      }
                      sx={entityLinkButtonSx}
                    >
                      Ir a Stock de seguridad
                    </Typography>
                  </Grid>
                )}
                {data.relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION' && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stock de seguridad (por selección)
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick(
                          'SAFETY_STOCK_BULK_SELECTION',
                          data.relatedEntityId,
                          undefined,
                        )
                      }
                      sx={entityLinkButtonSx}
                    >
                      Ir a Stock de seguridad
                    </Typography>
                  </Grid>
                )}
                {data.relatedEntityType === 'PHYSICAL_COUNT' && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Control de Inventario Físico
                    </Typography>
                    <Typography
                      variant="body1"
                      component="button"
                      onClick={() =>
                        handleRelatedEntityClick('PHYSICAL_COUNT', data.relatedEntityId, undefined)
                      }
                      sx={entityLinkButtonSx}
                    >
                      Ir a sesión de conteo
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {(() => {
            const isBulkSelection =
              data.eventType === 'SAFETY_STOCK_UPDATED_BULK' &&
              (data.newValue?.source === 'BULK_SELECTION' ||
                data.relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION');
            const selectedPayload = data.newValue?.selectedItemsDetail;
            const selectedSafetyStockItems =
              isBulkSelection && Array.isArray(selectedPayload)
                ? (selectedPayload as Array<Record<string, unknown>>)
                : [];
            if (selectedSafetyStockItems.length === 0) {
              return null;
            }
            const formatStock = (value: unknown) =>
              value == null ? 'sin configurar' : String(value);
            return (
              <Paper elevation={0} sx={detailModalSectionPaperSx}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  Productos actualizados ({selectedSafetyStockItems.length})
                </Typography>
                <Divider sx={{ my: 2 }} />
                <StyledTableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
                  <StyledTable size="small">
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Producto</StyledTableHeadCell>
                        <StyledTableHeadCell>Código</StyledTableHeadCell>
                        <StyledTableHeadCell>Ubicación</StyledTableHeadCell>
                        <StyledTableHeadCell>Stock anterior</StyledTableHeadCell>
                        <StyledTableHeadCell>Stock nuevo</StyledTableHeadCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {selectedSafetyStockItems.map((item, index) => (
                        <StyledTableRow
                          hover
                          key={`${item.productId ?? index}-${item.locationId ?? index}`}
                        >
                          <StyledTableCell>{getFormattedValue(item.productName)}</StyledTableCell>
                          <StyledTableCell sx={{ fontFamily: 'monospace' }}>
                            {getFormattedValue(item.productCode)}
                          </StyledTableCell>
                          <StyledTableCell>{getFormattedValue(item.locationName)}</StyledTableCell>
                          <StyledTableCell>{formatStock(item.previousSafetyStock)}</StyledTableCell>
                          <StyledTableCell>{formatStock(item.newSafetyStock)}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </Paper>
            );
          })()}

          {(() => {
            const isRefundEvent =
              data.eventType === 'ORDER_REFUNDED' || data.eventType === 'ORDER_PARTIALLY_REFUNDED';
            const refundProductsPayload = data.newValue?.products;
            const returnedProducts =
              isRefundEvent && Array.isArray(refundProductsPayload)
                ? (refundProductsPayload as Array<Record<string, unknown>>)
                : [];
            if (returnedProducts.length === 0) {
              return null;
            }
            return (
              <Paper elevation={0} sx={detailModalSectionPaperSx}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  Piezas devueltas ({returnedProducts.length})
                </Typography>
                <Divider sx={{ my: 2 }} />
                <StyledTableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
                  <StyledTable size="small">
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Código único</StyledTableHeadCell>
                        <StyledTableHeadCell>Código catálogo</StyledTableHeadCell>
                        <StyledTableHeadCell>Producto</StyledTableHeadCell>
                        <StyledTableHeadCell align="right">Reembolso (Bs)</StyledTableHeadCell>
                        <StyledTableHeadCell>Motivo</StyledTableHeadCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {returnedProducts.map((item, index) => (
                        <StyledTableRow
                          hover
                          key={`${item.warehouseInventoryProductId ?? item.itemId ?? index}`}
                        >
                          <StyledTableCell sx={{ fontFamily: 'monospace' }}>
                            {getFormattedValue(item.productCodeUnique)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontFamily: 'monospace' }}>
                            {getFormattedValue(item.productCode)}
                          </StyledTableCell>
                          <StyledTableCell>{getFormattedValue(item.productName)}</StyledTableCell>
                          <StyledTableCell align="right">
                            {getFormattedValue(item.refundAmount)}
                          </StyledTableCell>
                          <StyledTableCell>{getFormattedValue(item.returnReason)}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </Paper>
            );
          })()}

          {(() => {
            const productsPayload = data.newValue?.products;
            const isRequestResolved = data.eventType === 'REQUEST_RESOLVED';
            const isRequestCreated = data.eventType === 'REQUEST_CREATED';
            const isInventoryRecordCompleted = data.eventType === 'INVENTORY_RECORD_COMPLETED';
            const requestProducts =
              (isRequestCreated || isRequestResolved) && Array.isArray(productsPayload)
                ? (productsPayload as Array<Record<string, unknown>>)
                : [];
            const registeredProducts =
              isInventoryRecordCompleted && Array.isArray(productsPayload)
                ? (productsPayload as Array<Record<string, unknown>>)
                : [];
            const productsToShow =
              requestProducts.length > 0 ? requestProducts : registeredProducts;
            if (productsToShow.length === 0) {
              return null;
            }
            const sectionTitle = isRequestResolved
              ? `Piezas resueltas (${productsToShow.length})`
              : isRequestCreated
                ? `Piezas solicitadas (${productsToShow.length})`
                : `Piezas registradas (${productsToShow.length})`;
            return (
              <Paper elevation={0} sx={detailModalSectionPaperSx}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  {sectionTitle}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <StyledTableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
                  <StyledTable size="small">
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Código único</StyledTableHeadCell>
                        <StyledTableHeadCell>Código catálogo</StyledTableHeadCell>
                        {isRequestResolved && (
                          <>
                            <StyledTableHeadCell>Resultado</StyledTableHeadCell>
                            <StyledTableHeadCell>Motivo rechazo</StyledTableHeadCell>
                          </>
                        )}
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {productsToShow.map((item, index) => (
                        <StyledTableRow hover key={`${item.productCodeUnique ?? index}`}>
                          <StyledTableCell sx={{ fontFamily: 'monospace' }}>
                            {getFormattedValue(item.productCodeUnique)}
                          </StyledTableCell>
                          <StyledTableCell>{getFormattedValue(item.productCode)}</StyledTableCell>
                          {isRequestResolved && (
                            <>
                              <StyledTableCell>
                                {getFormattedValue(item.itemStatus)}
                              </StyledTableCell>
                              <StyledTableCell>
                                {getFormattedValue(item.rejectionReason)}
                              </StyledTableCell>
                            </>
                          )}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </Paper>
            );
          })()}

          {/* Change Details */}
          {(() => {
            const filteredFields = getFilteredChangedFields();
            const isRolePermissionsUpdatedEvent = data.eventType === 'ROLE_PERMISSIONS_UPDATED';

            // Detectar si hay propiedades agrupadas (array) para eventos de propiedades desde WAREHOUSE
            const hasGroupedProperties =
              ['PROPERTY_ADDED', 'PROPERTY_UPDATED', 'PROPERTY_DELETED'].includes(data.eventType) &&
              (Array.isArray(data.newValue?.properties) ||
                Array.isArray(data.previousValue?.properties));

            return filteredFields.length > 0 ||
              hasGroupedProperties ||
              isRolePermissionsUpdatedEvent ? (
              <Paper elevation={0} sx={detailModalSectionPaperSx}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  Detalles de Cambios
                </Typography>
                <Divider sx={{ my: 2 }} />
                <StyledTableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
                  <StyledTable size="small" sx={{ minWidth: 0, tableLayout: 'fixed' }}>
                    <StyledTableHead>
                      <TableRow>
                        <StyledTableHeadCell>Campo</StyledTableHeadCell>
                        <StyledTableHeadCell>Valor Anterior</StyledTableHeadCell>
                        <StyledTableHeadCell>Valor Nuevo</StyledTableHeadCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {isRolePermissionsUpdatedEvent
                        ? [
                            <StyledTableRow hover key="roleName">
                              <StyledTableCell>Rol</StyledTableCell>
                              <StyledTableCell>
                                {getFormattedValue(
                                  data.previousValue?.roleName ?? data.previousValue?.name,
                                )}
                              </StyledTableCell>
                              <StyledTableCell>
                                {getFormattedValue(data.newValue?.roleName ?? data.newValue?.name)}
                              </StyledTableCell>
                            </StyledTableRow>,
                            <StyledTableRow hover key="totalPermissions">
                              <StyledTableCell>Permisos Totales</StyledTableCell>
                              <StyledTableCell>
                                {getFormattedValue(data.previousValue?.totalPermissions)}
                              </StyledTableCell>
                              <StyledTableCell>
                                {getFormattedValue(data.newValue?.totalPermissions)}
                              </StyledTableCell>
                            </StyledTableRow>,
                            <StyledTableRow hover key="addedPermissions">
                              <StyledTableCell>Permisos Agregados</StyledTableCell>
                              <StyledTableCell>-</StyledTableCell>
                              <StyledTableCell>
                                {formatRolePermissionList(data.newValue?.addedPermissions)}
                              </StyledTableCell>
                            </StyledTableRow>,
                            <StyledTableRow hover key="removedPermissions">
                              <StyledTableCell>Permisos Removidos</StyledTableCell>
                              <StyledTableCell>
                                {formatRolePermissionList(data.newValue?.removedPermissions)}
                              </StyledTableCell>
                              <StyledTableCell>-</StyledTableCell>
                            </StyledTableRow>,
                          ]
                        : hasGroupedProperties
                          ? // Renderizar propiedades agrupadas desde WAREHOUSE
                            (() => {
                              const previousProperties = data.previousValue?.properties || [];
                              const newProperties = data.newValue?.properties || [];

                              // Crear un mapa de propiedades por nombre para facilitar la búsqueda
                              const prevMap = new Map(
                                previousProperties.map((p: any) => [p.propertyName, p]),
                              );
                              const newMap = new Map(
                                newProperties.map((p: any) => [p.propertyName, p]),
                              );

                              // Obtener todos los nombres de propiedades únicos
                              const allPropertyNames = new Set([
                                ...previousProperties.map((p: any) => p.propertyName),
                                ...newProperties.map((p: any) => p.propertyName),
                              ]);

                              return Array.from(allPropertyNames).map((propName: string) => {
                                const prevProp = prevMap.get(propName) as any;
                                const newProp = newMap.get(propName) as any;

                                // Determinar el valor anterior y nuevo
                                const prevValue =
                                  prevProp?.previousValue ?? prevProp?.propertyValue ?? '-';
                                const newValue = newProp?.newValue ?? newProp?.propertyValue ?? '-';

                                return (
                                  <StyledTableRow hover key={propName}>
                                    <StyledTableCell>
                                      {formatPropertyName(propName)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      {getFormattedValue(prevValue)}
                                    </StyledTableCell>
                                    <StyledTableCell>{getFormattedValue(newValue)}</StyledTableCell>
                                  </StyledTableRow>
                                );
                              });
                            })()
                          : // Lógica existente para campos individuales
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            filteredFields.flatMap((field, fieldIndex) => {
                              // Para eventos de propiedades, buscar valores en la estructura correcta
                              let previousVal: any = undefined;
                              let newVal: any = undefined;

                              if (
                                ['PROPERTY_ADDED', 'PROPERTY_UPDATED', 'PROPERTY_DELETED'].includes(
                                  data.eventType,
                                )
                              ) {
                                // Manejar el caso especial de 'properties' (array de múltiples etiquetas)
                                if (field === 'properties') {
                                  previousVal = data.previousValue?.properties;
                                  newVal = data.newValue?.properties;

                                  // Si hay un array de propiedades, renderizar cada una en una fila separada
                                  if (Array.isArray(newVal) && newVal.length > 0) {
                                    return newVal.map((prop: any, propIndex: number) => (
                                      <StyledTableRow hover key={`${field}-${propIndex}`}>
                                        <StyledTableCell>
                                          {propIndex === 0 ? 'Etiqueta Agregada' : ''}
                                        </StyledTableCell>
                                        <StyledTableCell>-</StyledTableCell>
                                        <StyledTableCell>
                                          {prop.propertyName}{' '}
                                          {prop.propertyId ? `(ID: ${prop.propertyId})` : ''}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    ));
                                  } else if (Array.isArray(previousVal) && previousVal.length > 0) {
                                    return previousVal.map((prop: any, propIndex: number) => (
                                      <StyledTableRow hover key={`${field}-${propIndex}`}>
                                        <StyledTableCell>
                                          {propIndex === 0 ? 'Etiqueta Removida' : ''}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                          {prop.propertyName}{' '}
                                          {prop.propertyId ? `(ID: ${prop.propertyId})` : ''}
                                        </StyledTableCell>
                                        <StyledTableCell>-</StyledTableCell>
                                      </StyledTableRow>
                                    ));
                                  }
                                  // Si no hay array, retornar array vacío para que flatMap lo maneje
                                  return [];
                                }

                                // Para otros campos de propiedades, los valores pueden estar en diferentes lugares
                                previousVal = data.previousValue?.[field];
                                newVal = data.newValue?.[field];

                                // Si no está en el campo directo, buscar en la estructura de propiedades
                                if (previousVal === undefined && data.previousValue) {
                                  // Buscar en campos relacionados (ej: previousName, previousDescription)
                                  const relatedField = `previous${field.charAt(0).toUpperCase() + field.slice(1)}`;
                                  previousVal =
                                    data.previousValue[relatedField] ?? data.previousValue[field];
                                }

                                if (newVal === undefined && data.newValue) {
                                  // Para newValue, buscar en campos específicos de propiedades
                                  if (field === 'name' && data.newValue.propertyName) {
                                    newVal = data.newValue.propertyName;
                                  } else if (field === 'color' && data.newValue.propertyColor) {
                                    newVal = data.newValue.propertyColor;
                                  } else {
                                    newVal = data.newValue[field];
                                  }
                                }

                                // Si es propertyValue, tomar del campo dedicado
                                if (field === 'propertyValue') {
                                  newVal = data.newValue?.propertyValue;
                                  previousVal = data.previousValue?.propertyValue ?? previousVal;
                                }

                                // Si es materialId, siempre mostrar el ID, y opcionalmente el nombre si está disponible
                                if (field === 'materialId') {
                                  // Obtener el ID del material (prioridad: materialId directo, luego propertyValue)
                                  const newId =
                                    data.newValue?.materialId ??
                                    data.newValue?.propertyValue ??
                                    newVal;
                                  const prevId =
                                    data.previousValue?.materialId ??
                                    data.previousValue?.propertyValue ??
                                    previousVal;

                                  // Obtener el nombre del material si está disponible
                                  const newName =
                                    data.newValue?.materialName ?? data.newValue?.propertyName;
                                  const prevName =
                                    data.previousValue?.materialName ??
                                    data.previousValue?.propertyName;

                                  // Formatear: mostrar ID siempre, y nombre si está disponible
                                  newVal =
                                    newId !== undefined && newId !== null
                                      ? newName
                                        ? `${newName} (ID: ${newId})`
                                        : String(newId)
                                      : (newName ?? '-');
                                  previousVal =
                                    prevId !== undefined && prevId !== null
                                      ? prevName
                                        ? `${prevName} (ID: ${prevId})`
                                        : String(prevId)
                                      : (prevName ?? '-');
                                }
                              } else {
                                // Para otros eventos, usar los valores directamente
                                previousVal = data.previousValue?.[field];
                                newVal = data.newValue?.[field];

                                // Manejo especial para tagIds: preferir tagNames si está disponible
                                if (field === 'tagIds') {
                                  // Si hay tagNames, usarlos en lugar de los IDs
                                  if (data.newValue?.tagNames) {
                                    newVal = data.newValue.tagNames;
                                  }
                                  if (data.previousValue?.tagNames) {
                                    previousVal = data.previousValue.tagNames;
                                  }
                                }
                              }

                              return (
                                <StyledTableRow hover key={field}>
                                  <StyledTableCell>{formatFieldName(field)}</StyledTableCell>
                                  <StyledTableCell>
                                    {getFormattedValue(previousVal)}
                                  </StyledTableCell>
                                  <StyledTableCell>{getFormattedValue(newVal)}</StyledTableCell>
                                </StyledTableRow>
                              );
                            })}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </Paper>
            ) : null;
          })()}

          {/* Change Summary */}
          {data.changeSummary && (
            <Paper elevation={0} sx={detailModalSectionPaperSx}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Resumen de Cambios
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" color="text.primary">
                {data.changeSummary}
              </Typography>
            </Paper>
          )}

          {/* Notes */}
          {(() => {
            // Generate message from structured data (eventType + newValue/previousValue + notes)
            const notesMessage = generateHistoryMessage(data);
            return notesMessage ? (
              <Paper elevation={0} sx={detailModalSectionPaperSx}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  Notas
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" color="text.primary">
                  {notesMessage}
                </Typography>
              </Paper>
            ) : null;
          })()}

          {/* Transfer Locations */}
          {(data.sourceLocation || data.destinationLocation) && (
            <Paper elevation={0} sx={[detailModalSectionPaperSx, { mb: 0 }]}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', color: 'text.primary' }}
              >
                <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                Ubicaciones de Transferencia
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {data.sourceLocation && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Origen
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {data.sourceLocation.name}
                    </Typography>
                  </Grid>
                )}
                {data.destinationLocation && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Destino
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {data.destinationLocation.name}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </Box>
      ) : null}
    </DetailModal>
  );
};

export default ProductMovementHistoryDetailModal;
